import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, ExternalLink, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import client, { apiError } from './adminApi';
import { RESOURCES } from './resources';
import { ConfirmDialog, Pagination, StatusBadge } from './components/ui';
import { getPath } from './components/fields';
import { useAdminAuth } from './AdminAuth';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function ResourceListPage() {
  const { resource } = useParams();
  const cfg = RESOURCES[resource];
  const { user } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const canDelete = ['owner', 'administrator'].includes(user?.role);

  const removeItem = async () => {
    if (!toDelete) return;
    try {
      await client.delete(`/admin/content/${resource}/${getPath(toDelete, cfg.idField)}`, { params: { confirm: true } });
      setToDelete(null);
      load();
    } catch (err) {
      setToDelete(null);
      setError(apiError(err, 'Could not delete the record.'));
    }
  };
  const extraFilter = Object.fromEntries(
    (cfg?.filters || []).map((f) => [f.key, searchParams.get(f.key) || '']).filter(([, v]) => v)
  );

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await client.get(`/admin/content/${resource}`, {
        params: { q: q || undefined, status: status || undefined, page, per_page: 20 },
      });
      let items = res.data.items;
      // Preset type filters (Services/Systems sidebar entries) are applied client-side
      items = items.filter((it) =>
        Object.entries(extraFilter).every(([k, v]) => getPath(it, k) === v));
      setData({ ...res.data, items });
    } catch (err) {
      setError(apiError(err, 'Could not load records.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, q, status, page]);

  useEffect(() => { load(); }, [load]);

  if (!cfg) return <p className="text-sm text-red-700">Unknown content type.</p>;

  return (
    <div data-testid={`admin-list-${resource}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">{cfg.label}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {data ? `${data.total} records` : 'Loading…'}
            {extraFilter.itemType && ` · filtered by type: ${extraFilter.itemType}`}
          </p>
        </div>
        <Link
          to={`/admin/content/${resource}/new`}
          data-testid="admin-new-record"
          className="inline-flex items-center gap-2 rounded-md bg-navy-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> New {cfg.singular}
        </Link>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder={`Search ${cfg.label.toLowerCase()}…`} aria-label={`Search ${cfg.label}`}
            data-testid="admin-search"
            className="h-10 w-64 rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
          />
        </div>
        {!cfg.noWorkflow && (
          <select
            value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            aria-label="Filter by status" data-testid="admin-status-filter"
            className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-navy-900"
          >
            <option value="">All statuses</option>
            {['draft', 'in_review', 'scheduled', 'published', 'archived'].map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        )}
        {(cfg.filters || []).map((f) => (
          <select
            key={f.key}
            value={searchParams.get(f.key) || ''}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set(f.key, e.target.value); else next.delete(f.key);
              setSearchParams(next, { replace: true });
            }}
            aria-label={`Filter by ${f.label}`}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-navy-900"
          >
            <option value="">All {f.label.toLowerCase()}</option>
            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
      </div>

      {error && <p role="alert" className="mt-4 text-sm font-semibold text-red-700">{error}</p>}

      {!data ? (
        <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center" data-testid="admin-empty">
          <p className="text-sm font-semibold text-slate-500">No records found. Create the first one.</p>
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                {cfg.columns.map((c) => <th key={c} scope="col" className="px-4 py-3">{c.replace(/([A-Z])/g, ' $1')}</th>)}
                {!cfg.noWorkflow && !cfg.noSeo && <th scope="col" className="px-4 py-3">SEO</th>}
                <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const rowId = getPath(item, cfg.idField);
                return (
                <Fragment key={rowId}>
                <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  {cfg.columns.map((c) => (
                    <td key={c} className="px-4 py-3 align-middle">
                      {c === 'status' ? (
                        <StatusBadge status={item.status} />
                      ) : c.endsWith('At') || c === 'order' || c === 'displayOrder' || c === 'hitCount' ? (
                        <span className="text-slate-600">{c.endsWith('At') ? fmtDate(getPath(item, c)) : getPath(item, c) ?? '—'}</span>
                      ) : Array.isArray(getPath(item, c)) ? (
                        <span className="text-slate-600">{getPath(item, c).join(', ')}</span>
                      ) : (
                        <span className="font-semibold text-navy-900">{String(getPath(item, c) ?? '—')}</span>
                      )}
                    </td>
                  ))}
                  {!cfg.noWorkflow && !cfg.noSeo && (
                    <td className="px-4 py-3">
                      {item.seoWarnings?.length > 0 && (
                        <span className="text-xs font-bold text-amber-700" title={item.seoWarnings.map((w) => w.message).join('; ')}>
                          {item.seoWarnings.length} warning{item.seoWarnings.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {cfg.overview && (
                        <button
                          type="button"
                          onClick={() => setExpanded(expanded === rowId ? null : rowId)}
                          aria-expanded={expanded === rowId}
                          aria-label={`Toggle overview for ${getPath(item, cfg.columns[0])}`}
                          data-testid={`overview-toggle-${rowId}`}
                          className="text-slate-400 transition-colors hover:text-navy-900"
                        >
                          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expanded === rowId ? 'rotate-90' : ''}`} aria-hidden="true" />
                        </button>
                      )}
                      <Link
                        to={`/admin/content/${resource}/${getPath(item, cfg.idField)}/edit`}
                        data-testid={`edit-${getPath(item, cfg.idField)}`}
                        className="text-sm font-bold text-navy-900 underline-offset-2 hover:underline"
                      >
                        Edit
                      </Link>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => setToDelete(item)}
                          data-testid={`delete-${getPath(item, cfg.idField)}`}
                          aria-label={`Delete ${getPath(item, cfg.columns[0])}`}
                          className="text-slate-400 transition-colors hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {cfg.overview && expanded === rowId && (
                  <tr data-testid={`overview-${rowId}`}>
                    <td colSpan={cfg.columns.length + 2} className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
                      <CaseStudyOverview item={item} editTo={`/admin/content/${resource}/${rowId}/edit`} />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={page} perPage={20} total={data.total} onPage={setPage} />}

      <ConfirmDialog
        open={!!toDelete}
        title={`Delete this ${cfg.singular.toLowerCase()}?`}
        message={`“${toDelete ? String(getPath(toDelete, cfg.columns[0]) ?? '') : ''}” will be permanently deleted. This cannot be undone — consider archiving instead if you may need it later.`}
        destructive
        confirmLabel="Delete Permanently"
        onCancel={() => setToDelete(null)}
        onConfirm={removeItem}
      />
    </div>
  );
}

// At-a-glance overview panel for case studies (cfg.overview). Shows the
// summary, key metadata, a content-completeness checklist (so unfinished
// studies are obvious while writing), and quick links.
function CaseStudyOverview({ item, editTo }) {
  const checks = [
    ['Challenge', !!item.challenge],
    ['Solution', !!item.solution],
    ['Outcomes', (item.outcomes || []).length > 0],
    ['Image', !!item.image?.src],
    ['Gallery', (item.gallery || []).length > 0],
    ['Metrics', (item.metrics || []).length > 0],
    ['Testimonial', !!item.testimonial?.quote],
    ['SEO', !!(item.seo?.title && item.seo?.description)],
  ];
  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      {item.image?.src && (
        <img src={item.image.src} alt="" className="h-24 w-36 shrink-0 rounded-lg border border-slate-200 object-cover" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-relaxed text-slate-700" data-testid="overview-summary">{item.summary || 'No summary yet.'}</p>
        <p className="mt-2 text-xs font-semibold text-slate-500">
          {[
            item.industry,
            (item.categories || []).join(', ') || null,
            item.featured ? 'Featured' : null,
            `Order ${item.order ?? '—'}`,
          ].filter(Boolean).join(' · ')}
        </p>
        <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Content completeness" data-testid="overview-completeness">
          {checks.map(([label, ok]) => (
            <li key={label} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ok ? 'bg-teal-100 text-teal-800' : 'bg-amber-100 text-amber-800'}`}>
              {label} {ok ? '✓' : 'missing'}
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap gap-4">
          <Link to={editTo} className="text-xs font-bold text-navy-900 underline-offset-2 hover:underline">Edit content</Link>
          {item.slug && item.status === 'published' && (
            <a href={`/case-studies/${item.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-navy-900 underline-offset-2 hover:underline" data-testid="overview-view-public">
              View public page <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

